import { useLocalStorageState } from "@istari-frontend/shared";
import type * as React from "react";
import { useRef, useState } from "react";

export interface AiChatWindowSize {
  width: number;
  height: number;
}

export const AI_CHAT_WINDOW_DEFAULT_SIZE: AiChatWindowSize = { width: 480, height: 600 };
export const AI_CHAT_WINDOW_MIN_SIZE: AiChatWindowSize = { width: 320, height: 400 };

const STORAGE_KEY = "istari-ai-chat-window-size";
const VIEWPORT_MARGIN = 24;
const KEYBOARD_STEP = 16;

export function clampAiChatWindowSize(size: AiChatWindowSize, viewport: AiChatWindowSize): AiChatWindowSize {
  const maxWidth = Math.max(AI_CHAT_WINDOW_MIN_SIZE.width, viewport.width - VIEWPORT_MARGIN * 2);
  const maxHeight = Math.max(AI_CHAT_WINDOW_MIN_SIZE.height, viewport.height - VIEWPORT_MARGIN * 2);
  return {
    width: Math.round(Math.min(Math.max(size.width, AI_CHAT_WINDOW_MIN_SIZE.width), maxWidth)),
    height: Math.round(Math.min(Math.max(size.height, AI_CHAT_WINDOW_MIN_SIZE.height), maxHeight)),
  };
}

interface DragOrigin {
  pointerX: number;
  pointerY: number;
  size: AiChatWindowSize;
}

export interface AiChatResizeHandleProps {
  onPointerDown: (event: React.PointerEvent<HTMLElement>) => void;
  onPointerMove: (event: React.PointerEvent<HTMLElement>) => void;
  onPointerUp: (event: React.PointerEvent<HTMLElement>) => void;
  onPointerCancel: (event: React.PointerEvent<HTMLElement>) => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void;
  onDoubleClick: () => void;
}

export function useAiChatWindowSize(): {
  size: AiChatWindowSize;
  isResizing: boolean;
  resizeHandleProps: AiChatResizeHandleProps;
} {
  const [storedSize, setStoredSize] = useLocalStorageState<AiChatWindowSize>(STORAGE_KEY, AI_CHAT_WINDOW_DEFAULT_SIZE);
  const [dragSize, setDragSize] = useState<AiChatWindowSize | null>(null);
  const dragOrigin = useRef<DragOrigin | null>(null);

  const onPointerDown = (event: React.PointerEvent<HTMLElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    // preventDefault suppresses click-to-focus, so focus explicitly to keep keyboard resize reachable.
    event.currentTarget.focus();
    dragOrigin.current = { pointerX: event.clientX, pointerY: event.clientY, size: storedSize };
  };

  const onPointerMove = (event: React.PointerEvent<HTMLElement>) => {
    if (!dragOrigin.current) return;
    setDragSize(sizeFromDrag(dragOrigin.current, event));
  };

  const onPointerUp = (event: React.PointerEvent<HTMLElement>) => {
    const origin = dragOrigin.current;
    if (!origin) return;
    dragOrigin.current = null;
    setDragSize(null);
    setStoredSize(sizeFromDrag(origin, event));
  };

  const onPointerCancel = () => {
    dragOrigin.current = null;
    setDragSize(null);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    const delta = keyboardDelta(event.key);
    if (!delta) return;
    event.preventDefault();
    setStoredSize(
      clampAiChatWindowSize(
        { width: storedSize.width + delta.width, height: storedSize.height + delta.height },
        viewportSize(),
      ),
    );
  };

  const onDoubleClick = () => setStoredSize(AI_CHAT_WINDOW_DEFAULT_SIZE);

  return {
    size: dragSize ?? storedSize,
    isResizing: dragSize !== null,
    resizeHandleProps: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onKeyDown, onDoubleClick },
  };
}

// The window is anchored bottom-right, so dragging the top-left handle up or left grows it.
function sizeFromDrag(origin: DragOrigin, event: React.PointerEvent<HTMLElement>): AiChatWindowSize {
  return clampAiChatWindowSize(
    {
      width: origin.size.width + (origin.pointerX - event.clientX),
      height: origin.size.height + (origin.pointerY - event.clientY),
    },
    viewportSize(),
  );
}

function keyboardDelta(key: string): AiChatWindowSize | null {
  switch (key) {
    case "ArrowLeft":
      return { width: KEYBOARD_STEP, height: 0 };
    case "ArrowRight":
      return { width: -KEYBOARD_STEP, height: 0 };
    case "ArrowUp":
      return { width: 0, height: KEYBOARD_STEP };
    case "ArrowDown":
      return { width: 0, height: -KEYBOARD_STEP };
    default:
      return null;
  }
}

function viewportSize(): AiChatWindowSize {
  return { width: window.innerWidth, height: window.innerHeight };
}
